// SecureJourneySection.jsx
import { useEffect, useRef, useState } from "react";

/* ── Icons ── */
const WifiIcon = () => (
  <svg
    className="w-3.5 h-3.5 text-gray-600"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const ShieldIcon = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const BoltIcon = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const DatabaseIcon = () => (
  <svg
    className="w-6 h-6"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const LockIcon = () => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const nodes = [
  { id: "device", label: "Your Device", icon: <PhoneIcon />, color: "blue" },
  {
    id: "encrypt",
    label: "Encryption Layer",
    icon: <ShieldIcon />,
    color: "gray",
  },
  { id: "tunnel", label: "Secure Tunnel", icon: <BoltIcon />, color: "purple" },
  {
    id: "server",
    label: "India Server",
    icon: <DatabaseIcon />,
    color: "green",
  },
];

const floatingChars = ["0x", "1A", "F3", "9B", "E7", "C4", "2D", "8F"];

export default function SecureJourneySection() {
  const [isAnimating, setIsAnimating] = useState(true);
  const [activeNode, setActiveNode] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [packetPosition, setPacketPosition] = useState(0);
  const [showPacket, setShowPacket] = useState(false);
  const [litLogs, setLitLogs] = useState([false, false, false, false]);

  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const hasRun = useRef(false);
  const sectionRef = useRef(null);

  const logs = [
    "Device detected",
    "AES-256 encryption applied",
    "TLS tunnel established",
    "Delivered to IN server",
  ];

  const startJourney = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setIsAnimating(true);
    setProgress(0);
    setPacketPosition(0);
    setShowPacket(false);
    setActiveNode(-1);
    setLitLogs([false, false, false, false]);
    startTimeRef.current = null;

    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
        requestAnimationFrame(animate);
        return;
      }

      const elapsed = timestamp - startTimeRef.current;
      const duration = 3000; // 3 seconds animation
      let newProgress = Math.min(1, elapsed / duration);

      setProgress(newProgress);
      setPacketPosition(newProgress * 100);

      // Show packet after slight delay
      if (elapsed > 200 && !showPacket) {
        setShowPacket(true);
      }

      // Update active node based on progress
      if (newProgress < 0.25) {
        if (activeNode !== 0) setActiveNode(0);
        if (!litLogs[0]) setLitLogs([true, false, false, false]);
      } else if (newProgress < 0.5) {
        if (activeNode !== 1) setActiveNode(1);
        if (!litLogs[1]) setLitLogs([true, true, false, false]);
      } else if (newProgress < 0.75) {
        if (activeNode !== 2) setActiveNode(2);
        if (!litLogs[2]) setLitLogs([true, true, true, false]);
      } else {
        if (activeNode !== 3) setActiveNode(3);
        if (!litLogs[3]) setLitLogs([true, true, true, true]);
      }

      if (newProgress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        setShowPacket(false);
        setActiveNode(-1);
        setIsAnimating(false);
        animationRef.current = null;
        startTimeRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  // Auto-start on scroll into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasRun.current) {
          hasRun.current = true;
          setTimeout(() => startJourney(), 300);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const getNodePosition = (index) => {
    const positions = [5, 35, 65, 95];
    return positions[index];
  };

  return (
    <section
      ref={sectionRef}
      className="bg-[#f5f7fa] px-6 py-20 flex flex-col items-center"
    >
      {/* Badge */}
      {/* inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-[13px] font-medium text-gray-700 mb-6 */}
      <div className="Data_Secure_Journey_Badge">
        <WifiIcon />
        End-to-End Encryption
      </div>

      {/* Heading */}
      {/* text-[clamp(28px,5vw,56px)] font-extrabold tracking-[-0.03em] leading-[1.1] text-center mb-4 */}
      <h2 className="Data_Secure_Journey_Head">
        <span className="text-[#000000]">Watch Your Data's </span>
        <span className="text-[#0F7B6C]">Secure Journey</span>
      </h2>
      {/* text-[15px] text-gray-500 text-center leading-[1.7] max-w-[620px] mb-11 */}
      <p className="Data_Secure_Journey_Para">
        Every task, note, and document gets encrypted on your device before
        leaving. Watch how your data transforms into unbreakable cipher text as
        it travels to our India-based servers.
      </p>

      {/* Main Visualization Card */}
      <div
        // w-full max-w-[1060px] rounded-[24px] border border-white/80 px-10 py-14 relative overflow-hidden
        className="Visualization_Card"
        style={{
          background:
            "linear-gradient(135deg,#dce8f5 0%,#e8f0f8 30%,#e8f5ee 70%,#d8f0e4 100%)",
        }}
      >
        {/* Track Line */}
        <div className="absolute top-1/2 left-10 right-10 h-[3px] bg-blue-200/40 rounded-full -translate-y-1/2" />

        {/* Progress Fill */}
        <div
          className="absolute top-1/2 left-10 h-[3px] rounded-full -translate-y-1/2 transition-all duration-100"
          style={{
            width: `${progress * 90}%`,
            background:
              "linear-gradient(90deg,#4A90D9 0%,#7C4DCA 50%,#2EAD72 100%)",
          }}
        />

        {/* Data Packet */}
        {showPacket && (
          <div
            className="absolute top-1/2 w-[12px] h-[12px] rounded-full bg-white z-20 pointer-events-none transition-all duration-100"
            style={{
              left: `calc(10% + ${packetPosition * 0.8}%)`,
              transform: "translate(-50%, -50%)",
              boxShadow: "0 0 0 3px #7C4DCA, 0 0 12px rgba(124,77,202,0.6)",
            }}
          />
        )}

        {/* Nodes */}
        <div className="relative z-10 flex items-center justify-between px-2">
          {nodes.map((node, i) => (
            <div key={node.id} className="flex flex-col items-center gap-3.5">
              <div className="relative">
                <div
                  className={`w-[60px] h-[60px] rounded-[16px] flex items-center justify-center shadow-lg transition-all duration-300 ${
                    activeNode === i ? "scale-110" : "scale-100"
                  }`}
                  style={{
                    background:
                      activeNode === i
                        ? `linear-gradient(135deg, ${node.color === "blue" ? "#3B82F6" : node.color === "purple" ? "#A855F7" : node.color === "green" ? "#10B981" : "#6B7280"}20, ${node.color === "blue" ? "#3B82F6" : node.color === "purple" ? "#A855F7" : node.color === "green" ? "#10B981" : "#6B7280"}40)`
                        : `linear-gradient(135deg, ${node.color === "blue" ? "#DBEAFE" : node.color === "purple" ? "#F3E8FF" : node.color === "green" ? "#D1FAE5" : "#F3F4F6"}, ${node.color === "blue" ? "#BFDBFE" : node.color === "purple" ? "#E9D5FF" : node.color === "green" ? "#A7F3D0" : "#E5E7EB"})`,
                    border:
                      activeNode === i
                        ? `2px solid ${node.color === "blue" ? "#3B82F6" : node.color === "purple" ? "#A855F7" : node.color === "green" ? "#10B981" : "#6B7280"}`
                        : "none",
                  }}
                >
                  <div
                    className={`${activeNode === i ? `text-${node.color === "blue" ? "blue" : node.color === "purple" ? "purple" : node.color === "green" ? "green" : "gray"}-600` : `text-${node.color === "blue" ? "blue" : node.color === "purple" ? "purple" : node.color === "green" ? "green" : "gray"}-500`}`}
                  >
                    {node.icon}
                  </div>
                </div>
                {activeNode === i && (
                  <div
                    className={`absolute inset-[-5px] rounded-[20px] border-2 border-${node.color === "blue" ? "blue" : node.color === "purple" ? "purple" : node.color === "green" ? "green" : "gray"}-400 animate-ping opacity-60`}
                    style={{ animationDuration: "1s" }}
                  />
                )}
              </div>
              <span className="text-[13px] font-semibold text-gray-700 whitespace-nowrap">
                {node.label}
              </span>
            </div>
          ))}
        </div>

        {/* Center Animation - Rotating Hexagon with Lock */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          {/* Rotating Hexagon Shield */}
          <div
            className="relative w-32 h-32"
            style={{
              animation: isAnimating ? "spin 20s linear infinite" : "none",
            }}
          >
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
            >
              <defs>
                <linearGradient
                  id="shieldGrad"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#093A5B" stopOpacity="0.4" />
                  <stop offset="50%" stopColor="#A855F7" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#10B981" stopOpacity="0.4" />
                </linearGradient>
              </defs>
              <polygon
                points="50,5 90,25 90,75 50,95 10,75 10,25"
                fill="url(#shieldGrad)"
                stroke="#093A5B"
                strokeWidth="1.5"
              />
              <polygon
                points="50,15 80,30 80,70 50,85 20,70 20,30"
                fill="none"
                stroke="#A855F7"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
            </svg>
          </div>

          {/* Stationary Lock Icon */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#093A5B] flex items-center justify-center shadow-xl">
            <div className="text-white">
              <LockIcon />
            </div>
          </div>
        </div>

        {/* Orbiting Particles */}
        {isAnimating && (
          <>
            {[...Array(8)].map((_, i) => {
              const angle = (i * 45 + Date.now() / 30) % 360;
              const rad = (angle * Math.PI) / 180;
              const radius = 70;
              const x = Math.cos(rad) * radius;
              const y = Math.sin(rad) * radius;
              const color = i % 2 === 0 ? "#093A5B" : "#A855F7";
              const size = 4 + (i % 3);
              return (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 rounded-full pointer-events-none"
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    background: color,
                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                    opacity: 0.6 + Math.sin(angle) * 0.3,
                    transition: "all 0.1s linear",
                  }}
                />
              );
            })}
          </>
        )}

        {/* Floating Encrypted Characters */}
        {isAnimating && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {floatingChars.map((char, i) => {
              const angle = (i * 45 + Date.now() / 20) % 360;
              const rad = (angle * Math.PI) / 180;
              const radius = 110;
              const x = Math.cos(rad) * radius;
              const y = Math.sin(rad) * radius;
              return (
                <div
                  key={i}
                  className="absolute top-1/2 left-1/2 text-xs font-mono font-bold"
                  style={{
                    color: i % 2 === 0 ? "#093A5B" : "#A855F7",
                    transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                    opacity: 0.5 + Math.sin(angle) * 0.3,
                    transition: "all 0.1s linear",
                  }}
                >
                  {char}
                </div>
              );
            })}
          </div>
        )}

        {/* Inner Glow Rings */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full border border-[#093A5B]/20 pointer-events-none"
          style={{
            animation: isAnimating ? "pulse 2s ease-in-out infinite" : "none",
          }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border border-purple-500/20 pointer-events-none"
          style={{
            animation: isAnimating
              ? "pulse 2.5s ease-in-out infinite reverse"
              : "none",
          }}
        />
      </div>

      {/* Stats Cards */}
      <div className="Secure_Stats_Cards">
        {/* bg-white rounded-2xl px-6 py-4 shadow-sm border border-gray-100 text-center min-w-[140px] */}
        <div className="Encryption_Class">
          <div className="text-2xl font-bold text-[#093A5B]">AES-256</div>
          <div className="text-xs text-gray-500 mt-1">Encryption Standard</div>
        </div>
        <div className="Encryption_Class">
          <div className="text-2xl font-bold text-[#093A5B]">&lt;50ms</div>
          <div className="text-xs text-gray-500 mt-1">Encryption Time</div>
        </div>
        <div className="Encryption_Class">
          <div className="text-2xl font-bold text-[#093A5B]">India</div>
          <div className="text-xs text-gray-500 mt-1">Data Location</div>
        </div>
      </div>

      {/* Status Logs */}
      {/* mt-8 flex flex-wrap gap-2 justify-center max-w-[1060px] */}
      <div className="Secure_Stats_Log_Cards">
        {logs.map((text, i) => (
          <div
            key={text}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-medium border transition-all duration-300 ${
              litLogs[i]
                ? "bg-blue-50 border-blue-300 text-[#1A3F5C]"
                : "bg-white border-gray-200 text-gray-400"
            }`}
          >
            <div
              className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${litLogs[i] ? "bg-blue-500" : "bg-gray-300"}`}
            />
            {text}
          </div>
        ))}
      </div>

      {/* Replay Button */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={startJourney}
          disabled={isAnimating}
          className="px-8 py-3 rounded-full font-semibold text-sm transition-all duration-300 shadow-lg bg-[#093A5B] text-white hover:bg-[#062C46] shadow-[#093A5B]/20 hover:shadow-[#093A5B]/30 hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Replay Journey
          </div>
        </button>
      </div>

      {/* Animation Keyframes */}
      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes pulse {
          0%,
          100% {
            opacity: 0.3;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.6;
            transform: translate(-50%, -50%) scale(1.05);
          }
        }
      `}</style>
    </section>
  );
}
